import { stripHtml, isEmptyHtml } from './stripHtml';

/**
 * Sort pesticide group associations alphanumerically by group name for the
 * client site display (spec "ordre d'affichage" — Groupes de pesticides :
 * ordre alphabétique ou alphanumérique). The admin keeps the input order, so
 * this must stay a display-time sort.
 *
 * Sorting whole association objects keeps the related fields (famille
 * chimique, risque de résistance, commentaires) aligned with their group.
 */
export const sortPesticideGroupAssociations = (associations) => {
  if (!Array.isArray(associations)) return [];
  return associations
    .map((assoc) => ({ assoc, key: stripHtml(assoc?.pesticide_group?.name || '') }))
    .sort((a, b) => a.key.localeCompare(b.key, 'fr', { numeric: true, sensitivity: 'base' }))
    .map(({ assoc }) => assoc);
};

/**
 * Concentration shown between parentheses next to the substance name.
 * Falls back to the free-text « Concentration autre » field when the percentage
 * is empty (ex. "1x10E10 conidies/mL"). Returns '-' when both are empty.
 */
export const formatConcentration = (percentValue, otherValue) => {
  const num = parseFloat(percentValue);
  // A zero percentage means "not expressed as a percentage" — the API sends the
  // Decimal as the string "0.0000" for those rows, and the real value lives in
  // the free-text field. Treat it as absent, not as "0 %".
  if (!isNaN(num) && num !== 0) {
    const formatted = num.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 4 });
    return `(${formatted} %)`;
  }
  const other = typeof otherValue === 'string' ? otherValue.trim() : '';
  return other !== '' ? `(${other})` : '-';
};

/**
 * One entry per pesticide group ('-' when the risk is empty) so the column
 * stays aligned with the groups. `color` null ⇒ default text colour.
 * Expects associations already sorted (sortPesticideGroupAssociations).
 */
export const getResistanceRiskData = (associations) => {
  if (!Array.isArray(associations) || associations.length === 0)
    return [{ name: '-', color: null }];
  return associations.map((assoc) => {
    const risk = assoc?.pesticide_group?.risk_of_resistance_development;
    return risk?.name ? { name: risk.name, color: risk.color || null } : { name: '-', color: null };
  });
};

/** One entry per pesticide group; empty HTML ("<p></p>") becomes '-'. */
export const getResistanceComments = (associations) => {
  if (!Array.isArray(associations) || associations.length === 0) return ['-'];
  return associations.map((assoc) => {
    const comment = assoc?.pesticide_group?.comment_on_resistance;
    return isEmptyHtml(comment) ? '-' : comment;
  });
};
